import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabaseClient } from '../../data/supabase-client';
import { reportError } from '../../lib/monitoring';
import { buildCapturePhotoViews, type CapturePhotoView } from '../../data/photos/photo-view';
import type { CapturePhotoRow } from '../../data/database.types';

/**
 * THE PREVIEW'S PHOTO SEAM — READ ONLY, AND ONE QUERY. M10 / S10-03B.
 *
 * **IT EXISTS BECAUSE THE PHOTO BOARD UPLOADS.** `useCapturePhotoBoard` runs
 * `runDelivery()` in an effect the moment it mounts. Mounting one per selected
 * Capture on a preview would start an upload pass per Capture against the one
 * shared per-owner queue — from a screen whose entire job is to look at
 * something. Nothing would be corrupted, and it would still be wrong: previewing
 * a report must not make a phone busy on a bad connection in a basement.
 *
 * **THERE IS NO WRITE SURFACE IN THIS FILE.** No `addPhotos`, no `remove`, no
 * `reclassify`, no `retryDelivery`, no staging store, no transport. A read, and
 * a view model.
 *
 * **ONE QUERY FOR THE WHOLE REPORT, AND THAT IS STRUCTURAL RATHER THAN AN
 * OPTIMISATION.** S10-02 reads one Capture at a time because each Capture's
 * section is its own screen. A preview must assemble the WHOLE document before
 * it can decide where the pages break, so every selected Capture's photos have
 * to be in hand at once — and a hook cannot be called in a loop. Reading them
 * together is the only shape that works, and it happens to cost one round trip
 * instead of a dozen.
 *
 * **STAGED PHOTOS ARE DELIBERATELY ABSENT, AND IT COSTS NOTHING.** Only photos
 * the SERVER has accepted belong in a client-facing report, and every attached
 * photo already has its display image in Storage — the pipeline uploads the
 * derivatives before it attaches the row. So an empty staging list changes what
 * can be rendered not at all.
 *
 * **AUTHORIZATION IS RLS.** `capture_photo_select_own` matches only the caller's
 * own rows, under forced row-level security; the id list narrows, it does not
 * protect.
 */
export interface ReportPhotosState {
  readonly status: 'loading' | 'ready' | 'failed';
  /** Capture id → its ACTIVE, ATTACHED photos. Removed evidence never appears. */
  readonly byCapture: ReadonlyMap<string, readonly CapturePhotoView[]>;
}

const EMPTY: ReadonlyMap<string, readonly CapturePhotoView[]> = new Map();

export function useReportPhotos(captureIds: readonly string[]): ReportPhotosState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>(
    client ? 'loading' : 'failed',
  );
  const [rows, setRows] = useState<readonly CapturePhotoRow[]>([]);

  // Stable across renders so a caller rebuilding its array does not re-query.
  const key = useMemo(() => [...captureIds].sort().join(','), [captureIds]);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const fetchPhotos = useCallback(async (): Promise<
    { kind: 'ready'; rows: CapturePhotoRow[] } | { kind: 'failed' }
  > => {
    if (!client) return { kind: 'failed' };
    const ids = key === '' ? [] : key.split(',');
    if (ids.length === 0) return { kind: 'ready', rows: [] };

    const { data, error } = await client
      .from('capture_photo')
      .select('*')
      .in('capture_id', ids)
      // Removed evidence is excluded here exactly as the Capture screen
      // excludes it — a photo Brandon removed must never reach a client.
      .is('removed_at', null)
      .order('position', { ascending: true });

    if (error) {
      reportError(error, 'reportPhotos.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client, key]);

  const apply = useCallback(
    (result: { kind: 'ready'; rows: CapturePhotoRow[] } | { kind: 'failed' }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setRows([]);
        setStatus('failed');
        return;
      }
      setRows(result.rows);
      setStatus('ready');
    },
    [],
  );

  const generation = useRef(0);
  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchPhotos();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchPhotos, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const byCapture = useMemo(() => {
    if (rows.length === 0) return EMPTY;
    const grouped = new Map<string, CapturePhotoRow[]>();
    for (const row of rows) {
      const list = grouped.get(row.capture_id) ?? [];
      list.push(row);
      grouped.set(row.capture_id, list);
    }
    const views = new Map<string, readonly CapturePhotoView[]>();
    for (const [captureId, captureRows] of grouped) {
      views.set(
        captureId,
        buildCapturePhotoViews({
          captureId,
          rows: captureRows,
          staged: [],
          activeClientPhotoUuid: null,
        }),
      );
    }
    return views;
  }, [rows]);

  return { status, byCapture };
}
